import { Button, DialogContent } from '@mui/material';
import { Interval, toDate } from 'date-fns';
import React, { ReactElement, useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Dialog, DialogProps, useRevokeDowntimeMutation, useRevokeLegacyDowntimeMutation } from '../';
import { Device, ForecastProperty, ForecastTarget } from '../../generated/graphql';
import { useLocale, useSnackbar } from '../../hooks';

interface DowntimeRevokeModalProps {
  device: Device;
  currentForecastProperty?: ForecastProperty;
  revokeDowntimeInterval: Interval | null;
  setRevokeDowntimeInterval: React.Dispatch<React.SetStateAction<Interval | null>>;
  onRevocation?: (date: string | number | Date | null) => void;
}

export function DowntimeRevokeModal({
  device,
  currentForecastProperty,
  revokeDowntimeInterval,
  setRevokeDowntimeInterval,
  onRevocation,
}: Readonly<DowntimeRevokeModalProps>): ReactElement<DowntimeRevokeModalProps> {
  const [t] = useTranslation();
  const { code: locale } = useLocale();
  const { showSuccess, showError } = useSnackbar();
  const [revokeLegacyDowntime] = useRevokeLegacyDowntimeMutation();
  const [revokeDowntime] = useRevokeDowntimeMutation();

  const resetRevokeModal = useCallback(() => {
    setRevokeDowntimeInterval(null);
  }, [setRevokeDowntimeInterval]);

  const handleDialogClose = useCallback<NonNullable<DialogProps['onClose']>>(
    (_event, reason) => {
      if (reason === 'escapeKeyDown' || reason === 'cancelClick') {
        resetRevokeModal();
      }
    },
    [resetRevokeModal],
  );

  const handleDialogConfirm = useCallback(async () => {
    if (!revokeDowntimeInterval) return;

    if (onRevocation) onRevocation(revokeDowntimeInterval.start);

    if (currentForecastProperty?.target === ForecastTarget.VPP)
      await revokeDowntime(
        device.internalId,
        toDate(revokeDowntimeInterval.start),
        toDate(revokeDowntimeInterval.end),
        {
          onError: (error) => showError(error),
          onCompleted: () => {
            showSuccess(t('DOWNTIME_CANCELED'));
            if (onRevocation) onRevocation(null);
          },
        },
      );
    else
      await revokeLegacyDowntime(device.id, toDate(revokeDowntimeInterval.start), toDate(revokeDowntimeInterval.end), {
        onError: (error) => showError(error),
        onCompleted: () => {
          showSuccess(t('DOWNTIME_CANCELED'));
          if (onRevocation) onRevocation(null);
        },
      });
    resetRevokeModal();
  }, [
    device.id,
    onRevocation,
    resetRevokeModal,
    revokeLegacyDowntime,
    revokeDowntimeInterval,
    showError,
    showSuccess,
    t,
  ]);

  return (
    <Dialog
      open={!!revokeDowntimeInterval}
      onClose={handleDialogClose}
      title={t('DOWNTIME_CANCEL')}
      actions={
        <>
          <Button variant="outlined" onClick={resetRevokeModal}>
            {t('DOWNTIME_KEEP')}
          </Button>
          <Button onClick={handleDialogConfirm}>{t('DOWNTIME_CANCEL')}</Button>
        </>
      }
    >
      <DialogContent>
        {revokeDowntimeInterval && (
          <Trans components={{ strong: <strong /> }}>
            {t('DOWNTIME_DIALOG', {
              device: device.externalName || device.name,
              start: toDate(revokeDowntimeInterval.start).toLocaleString(locale, {
                day: 'numeric',
                month: 'numeric',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
              }),
              end: toDate(revokeDowntimeInterval.end).toLocaleString(locale, {
                day: 'numeric',
                month: 'numeric',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
              }),
            })}
          </Trans>
        )}
      </DialogContent>
    </Dialog>
  );
}
